import React, { useState } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, ShieldX, LogOut, Upload } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { submitKyc, uploadKycDocument } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/farmway-logo.png';

const inputCls = 'border-white/15 bg-white/5 text-farmway-cream placeholder:text-farmway-cream/40 focus-visible:ring-farmway-gold';
const EMPTY = { fullName: '', dob: '', country: '', idType: 'Passport', idNumber: '', address: '' };

export default function KycGate() {
  const { user, setUser, logout } = useAuth();
  const [form, setForm] = useState({ ...EMPTY, fullName: user?.name || '' });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  if (user?.kyc?.status === 'rejected') {
    return (
      <div className="grid min-h-screen place-items-center bg-farmway-green px-5 py-28">
        <div className="w-full max-w-md rounded-3xl border border-white/10 bg-farmway-green-dark/70 p-8 text-center backdrop-blur-xl">
          <img src={logo} alt="Farmway" className="mx-auto h-14 w-14 rounded-2xl object-cover ring-1 ring-farmway-leaf/30" />
          <div className="mx-auto mt-6 grid h-12 w-12 place-items-center rounded-full bg-red-500/15 text-red-400">
            <ShieldX className="h-6 w-6" />
          </div>
          <h1 className="mt-5 display-xl text-3xl text-farmway-cream">Verification unsuccessful</h1>
          <p className="mt-3 text-farmway-cream/70" data-testid="kyc-rejected-message">
            We couldn&apos;t verify your submitted details.{user.kyc?.rejectReason ? ` Reason: ${user.kyc.rejectReason}.` : ''} Please contact the Farmway team to resolve this.
          </p>
          <button onClick={logout} className="mx-auto mt-6 flex items-center gap-2 text-sm text-farmway-cream/50 hover:text-farmway-cream">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </div>
    );
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim()) return toast.error('Please enter your full legal name');
    if (!form.idNumber.trim()) return toast.error('Please enter your ID number');
    if (!file) return toast.error('Please upload a photo or PDF of your ID');
    if (file.size > 10 * 1024 * 1024) return toast.error('ID document must be under 10 MB');
    setSubmitting(true);
    try {
      await uploadKycDocument(file);
      const res = await submitKyc(form);
      setUser(res.user);
      toast.success('Thanks, your details are submitted. Welcome to Farmway.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-farmway-green px-5 py-28">
      <div className="w-full max-w-xl rounded-3xl border border-white/10 bg-farmway-green-dark/70 p-8 backdrop-blur-xl sm:p-10">
        <img src={logo} alt="Farmway" className="h-12 w-12 rounded-2xl object-cover ring-1 ring-farmway-leaf/30" />
        <span className="eyebrow mt-6 block">Investor onboarding</span>
        <h1 className="mt-3 display-xl text-3xl text-farmway-cream">Complete your KYC</h1>
        <p className="mt-3 text-farmway-cream/70">
          A few quick details to verify your identity before accessing your portfolio and the marketplace.
        </p>

        <form onSubmit={onSubmit} className="mt-7 space-y-4" data-testid="kyc-form">
          <div className="space-y-1.5">
            <Label className="text-farmway-cream/80">Full legal name</Label>
            <Input data-testid="kyc-fullname" value={form.fullName} onChange={update('fullName')} placeholder="Jane A. Doe" className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-farmway-cream/80">Date of birth</Label>
              <Input type="date" value={form.dob} onChange={update('dob')} className={inputCls} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-farmway-cream/80">Country of residence</Label>
              <Input value={form.country} onChange={update('country')} placeholder="United Arab Emirates" className={inputCls} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-farmway-cream/80">ID type</Label>
              <select value={form.idType} onChange={update('idType')} className="h-10 w-full rounded-md border border-white/15 bg-white/5 px-3 text-sm text-farmway-cream focus:outline-none focus:ring-2 focus:ring-farmway-gold">
                {['Passport', 'National ID', "Driver's License"].map((o) => (
                  <option key={o} value={o} className="bg-farmway-green-dark">{o}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-farmway-cream/80">ID number</Label>
              <Input data-testid="kyc-idnumber" value={form.idNumber} onChange={update('idNumber')} placeholder="AB1234567" className={inputCls} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-farmway-cream/80">ID document</Label>
            <label className="flex cursor-pointer items-center gap-3 rounded-md border border-dashed border-white/20 bg-white/5 px-4 py-3 text-sm text-farmway-cream/70 transition-colors hover:border-farmway-gold/60">
              <Upload className="h-4 w-4 shrink-0 text-farmway-gold" />
              <span className="truncate">{file ? file.name : 'Upload a photo or PDF of your ID (max 10 MB)'}</span>
              <input data-testid="kyc-doc-input" type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
          </div>
          <div className="space-y-1.5">
            <Label className="text-farmway-cream/80">Residential address</Label>
            <Textarea rows={2} value={form.address} onChange={update('address')} placeholder="Street, city, country" className={`resize-none ${inputCls}`} />
          </div>
          <button type="submit" data-testid="kyc-submit" disabled={submitting} className="btn-gold w-full disabled:opacity-70">
            <ShieldCheck className="h-4 w-4" /> {submitting ? 'Submitting…' : 'Submit & continue'}
          </button>
        </form>

        <button onClick={logout} className="mx-auto mt-5 flex items-center gap-2 text-sm text-farmway-cream/50 hover:text-farmway-cream">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
        <p className="mt-4 text-center text-xs text-farmway-cream/40">Your details are stored securely and used only for investor verification.</p>
      </div>
    </div>
  );
}
